import { z } from "zod";

import { listQuerySchema } from "../common/list-query";
import { paginatedSchema } from "../common/pagination";
import { tenantScopeSchema } from "../common/tenant";

/** Decimal degrees. Both coordinates are optional, but must arrive together. */
export const latitudeSchema = z.number().min(-90).max(90);
export const longitudeSchema = z.number().min(-180).max(180);

export const siteSchema = z.object({
  id: z.uuid(),
  organizationId: z.uuid(),
  /**
   * Denormalized for display, the way Device carries its site and asset
   * names: a super admin's list spans every tenant, and rows with no
   * indication of which one they belong to are unreadable.
   */
  organizationName: z.string(),
  name: z.string(),
  address: z.string().nullable(),
  latitude: z.number().nullable(),
  longitude: z.number().nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
  counts: z.object({
    assets: z.number().int().nonnegative(),
    devices: z.number().int().nonnegative(),
    gateways: z.number().int().nonnegative(),
  }),
});

export type Site = z.infer<typeof siteSchema>;

export const createSiteRequestSchema = z
  .object({
    name: z.string().trim().min(2).max(120),
    address: z.string().trim().max(240).optional(),
    latitude: latitudeSchema.optional(),
    longitude: longitudeSchema.optional(),
  })
  .extend(tenantScopeSchema.shape)
  // Half a coordinate pair points at nothing, and silently storing it would
  // put a marker on the equator later.
  .refine((value) => (value.latitude === undefined) === (value.longitude === undefined), {
    message: "Latitude and longitude must be provided together.",
    path: ["latitude"],
  });

export type CreateSiteRequest = z.infer<typeof createSiteRequestSchema>;

export const updateSiteRequestSchema = z
  .object({
    name: z.string().trim().min(2).max(120).optional(),
    address: z.string().trim().max(240).nullable().optional(),
    latitude: latitudeSchema.nullable().optional(),
    longitude: longitudeSchema.nullable().optional(),
  })
  .refine(
    (value) => {
      const { latitude, longitude } = value;
      if (latitude === undefined && longitude === undefined) {
        return true;
      }
      if (latitude === null && longitude === null) {
        return true;
      }
      return typeof latitude === "number" && typeof longitude === "number";
    },
    {
      message: "Latitude and longitude must be provided together.",
      path: ["latitude"],
    },
  );

export type UpdateSiteRequest = z.infer<typeof updateSiteRequestSchema>;

export const listSitesQuerySchema = listQuerySchema;
export type ListSitesQuery = z.infer<typeof listSitesQuerySchema>;

export const siteListSchema = paginatedSchema(siteSchema);
export type SiteList = z.infer<typeof siteListSchema>;
